<template>
    <b-modal
        v-model="isVisible"
        size="lg"
        modal-class="vue-need-bootstrap"
        title="Variants Selector"
        lazy
        centered
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        @close="onClose"
    >
        <div v-if="step === 'selectVariants'">
            <div>
                <store-selector
                    v-if="user"
                    v-model="storeUrl"
                    :stores="user.Stores"
                    title="Select store to retrieve POD information"
                    include-no-billing
                />
            </div>
            <div v-if="loadingVariants" class="text-center">
                <b-spinner variant="primary" class="my-2" />
            </div>
            <div v-else>
                <variants-configurator
                    v-if="podService"
                    :pod-service="podService"
                    :selected-print-providers="selectedPrintProviders"
                    :selected-product-id="selectedProductId"
                    :selected-template-id="props.templateId"
                    :store="storeUrl"
                    :configurations="configurations"
                    :show-variants-details="true"
                    :allowed-placeholders="allowedPlaceholders"
                    @variants-selected="onVariantsSelected"
                    @delete-template-product="onDeleteTemplateProduct"
                />
            </div>
        </div>
        <div v-if="step === 'finalRevision'" class="col">
            <revision-step
                v-if="podService"
                :pod-service="podService"
                :product-title="productTitle"
                :provider-variants="selectedProviderVariants"
            />
            <b-btn variant="primary" class="float-right" @click="onSave">Save</b-btn>
        </div>
    </b-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, watch } from 'vue';
import store from '@/store';
import { BModal } from 'bootstrap-vue';
import { getPodAssignments } from '@/api/admin/templateProducts';
import { PrintProviderClass, SelectedProviderVariants } from '@/typings/printify/printProvider';
import { Placeholder, PlaceholderDimension } from './typings';
import { PodProviderConfiguration } from '../typings';
import VariantsConfigurator from '@/components/TemplateProductPodAssigner/VariantsConfigurator/VariantsConfigurator.vue';
import StoreSelector from '@/components/StoreSelector/StoreSelector.vue';
import { PodAssignment, PodService } from '@/api/typings/templateProducts';
import { printProviderFake } from '../printProviderFake';
import { getBlueprint, getPrintProviderById } from '@/api/storesApp/printify';
import { formatAspectRatio } from '@/utils/formatUtils';
import { Provider } from '@/utils/PodProvider.enum';
import RevisionStep from '@/components/TemplateProductPodAssigner/RevisionStep/RevisionStep.vue';
import { getProduct } from '@/api/storesApp/printful';

const selectedPrintProviders = ref<PrintProviderClass[] | undefined>();
const selectedProductId = ref<string>('');
const productTitle = ref<string>('');
const storeUrl = ref<string>('');
const configurations = ref<PodProviderConfiguration[]>([]);
const loadingVariants = ref<boolean>(true);
const podService = ref<PodService | undefined>(undefined);
const selectedProviderVariants = ref<SelectedProviderVariants[]>([]);
const selectedPodAssignments = ref<PodAssignment[]>([]);
const biggestPlaceholders = ref<PlaceholderDimension[]>([]);
const step: Ref<'selectVariants' | 'finalRevision'> = ref('selectVariants');

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'save', value: Record<string, unknown>): void;
    (event: 'close'): void;
}>();

const props = defineProps<{
    templateId: string;
}>();

const isVisible = ref(true);
const allowedPlaceholders: Ref<string[]> = ref([]);

const user = computed(() => store.state.user);

watch(isVisible, () => {
    emit('input', isVisible.value);
});

onMounted(async () => {
    await initialize();
});

const initialize = async () => {
    loadingVariants.value = true;
    try {
        const podAssignments = await getPodAssignments(props.templateId);
        if (!podAssignments.length) {
            return;
        }
        allowedPlaceholders.value = [...new Set(podAssignments.map((a) => a.placeholderId))];

        const printProviderId = podAssignments[0].printProviderId;
        const productId = podAssignments[0].productId;
        podService.value = podAssignments[0].podService;

        selectedProductId.value = productId;
        if (printProviderId) {
            let printProviderTitle = '';
            try {
                const printProvider = await getPrintProviderById(printProviderId);
                printProviderTitle = printProvider.title;
            } catch (error) {
                console.log('Error getting print provider', error);
            }
            selectedPrintProviders.value = [{ id: Number(printProviderId), title: printProviderTitle }];
        }
        configurations.value = [
            {
                productId,
                provider: podService.value,
                placeholders: podAssignments.map(({ variantId, printProviderId, placeholderId }) => ({
                    variantId,
                    printProviderId:
                        podService.value == Provider.PRINTFUL ? String(printProviderFake.id) : printProviderId,
                    placeholderId
                }))
            }
        ];
    } finally {
        loadingVariants.value = false;
    }
};

const onClose = () => {
    emit('close');
};

const onVariantsSelected = async (value: {
    configurations: PodProviderConfiguration[];
    biggestPlaceholders: Placeholder[];
    selectedProviderVariants: SelectedProviderVariants[];
}) => {
    if (!value.configurations.length) {
        return;
    }
    const productId = value.configurations[0].productId;
    const provider = value.configurations[0].provider;
    selectedPodAssignments.value = value.configurations[0].placeholders.map((ph) => ({
        id: 0,
        podService: provider,
        productId,
        printProviderId: ph.printProviderId,
        variantId: ph.variantId,
        placeholderId: ph.placeholderId
    }));
    selectedProviderVariants.value = value.selectedProviderVariants.map((prov) => {
        return {
            ...prov,
            printProviderName:
                selectedPrintProviders.value && selectedPrintProviders.value.length > 0
                    ? selectedPrintProviders.value[0].title
                    : ''
        };
    });

    if (podService.value == Provider.PRINTIFY) {
        biggestPlaceholders.value = value.biggestPlaceholders.map((x) => {
            return {
                id: x.position,
                width: x.width,
                height: x.height,
                aspectRatio: formatAspectRatio(x.width, x.height)
            };
        });
    } else {
        biggestPlaceholders.value = value.biggestPlaceholders.map((x) => {
            return {
                id: x.file?.type ?? x.position,
                width: x.width,
                height: x.height,
                dpi: x.dpi,
                aspectRatio: x.aspectRatio ?? formatAspectRatio(x.width, x.height)
            };
        });
    }
    try {
        productTitle.value =
            podService.value == Provider.PRINTIFY
                ? (await getBlueprint(Number(productId), storeUrl.value)).title
                : (await getProduct(productId, storeUrl.value)).product.title;
    } catch (error) {
        console.log('Error getting product tittle', error);
    }
    step.value = 'finalRevision';
};

const onDeleteTemplateProduct = () => {
    emit('close');
};

const onSave = async () => {
    const data = {
        podAssignments: selectedPodAssignments.value,
        biggestPlaceholders: biggestPlaceholders.value,
        storeUrl: storeUrl.value
    };
    emit('save', data);
};
</script>
